import { Box, Stack } from "@mui/material";
import { PanelErrorBoundary } from "components/common/error/PanelErrorBoundary";
import { ActiveChargeScheduleMaterials } from "components/identifyEventsTool/tool/ActiveChargeScheduleMaterials";
import {
  HotkeyDescription,
  HotkeyExplanation,
} from "components/identifyEventsTool/tool/HotkeyExplanation";
import { RailcarMovements } from "components/identifyEventsTool/tool/RailcarMovements";
import { RailcarTimeline } from "components/identifyEventsTool/tool/RailcarTimeline";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { LabellingOverlays } from "components/scrapEyeLabelling/flows/LabellingOverlays";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useHasPublishedChargeSchedules } from "hooks/useHasPublishedChargeSchedules";
import { useThrottledValue } from "hooks/useThrottledValue";
import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  LoadingWindowAssignment,
  HeatSummary,
  LabellerTimeline,
  RailcarSlotMaterialSpan,
  ReviewEvent,
  ReviewPeriodData,
} from "src/store/api/generatedApi";
import { useManualReviewEvents } from "./contexts/ManualReviewEventContext";
import { VerifyPeriodsFn } from "./hooks/useVerifySaveGuards";
import { ManualReviewAreaHotkeyModeToggle } from "./ManualReviewAreaHotkeyModeToggle";
import { useManualReviewHotkeys } from "./ManualReviewHotkeys";
import { ManualReviewPlayerSection } from "./ManualReviewPlayerSection";
import {
  MANUAL_REVIEW_SIDEBAR_WIDTH,
  ManualReviewSidebar,
  ManualReviewSidebarTab,
} from "./ManualReviewSidebar";
import { massEventsFromReviewEvents } from "./utils/eventUtils";
import { getNextEvent } from "./utils/formatters";

interface ManualReviewSurfaceProps {
  location: string;
  footageLocation?: string;
  reviewPeriods: ReviewPeriodData[];
  displayedReviewPeriods?: ReviewPeriodData[];
  allReviewPeriods: ReviewPeriodData[];
  viewingWindowStart: Dayjs;
  viewingWindowEnd: Dayjs;
  debouncedWindowStart: Dayjs;
  debouncedWindowEnd: Dayjs;
  displayStart?: Dayjs;
  displayEnd?: Dayjs;
  isEventsFetching: boolean;
  isVerifying: boolean;
  verifyPeriods: VerifyPeriodsFn;
  goToPeriod: (period: ReviewPeriodData) => void;
  verifyPeriodStarts?: string[];
  labellerTimelines: LabellerTimeline[];
  railcarSlotMaterials?: RailcarSlotMaterialSpan[];
  reviewEvents: ReviewEvent[];
  heats: HeatSummary[];
  loadingWindows: LoadingWindowAssignment[];
  dayStart: Dayjs;
  dayEnd: Dayjs;
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
  sidebarExtraTabs?: readonly ManualReviewSidebarTab[];
  navigationWindowMinutes?: number;
  extraHotkeys?: readonly HotkeyDescription[];
  children?: ReactNode;
}

export const ManualReviewSurface = ({
  location,
  footageLocation,
  reviewPeriods,
  displayedReviewPeriods,
  allReviewPeriods,
  viewingWindowStart,
  viewingWindowEnd,
  debouncedWindowStart,
  debouncedWindowEnd,
  displayStart,
  displayEnd,
  isEventsFetching,
  isVerifying,
  verifyPeriods,
  goToPeriod,
  verifyPeriodStarts,
  labellerTimelines,
  railcarSlotMaterials,
  reviewEvents,
  heats,
  loadingWindows,
  dayStart,
  dayEnd,
  highlightColorsByEventId,
  sidebarExtraTabs,
  navigationWindowMinutes,
  extraHotkeys,
  children,
}: ManualReviewSurfaceProps) => {
  const { t } = useTenantTranslation();
  const start = displayStart ?? viewingWindowStart;
  const end = displayEnd ?? viewingWindowEnd;

  const eventHotkeys: HotkeyDescription[] = useMemo(
    () => [
      { hotkeyId: "verifyWindow", description: t("verifyWindow") },
      { hotkeyId: "editWindow", description: t("editWindow") },
    ],
    [t]
  );

  const [verifyAction, setVerifyAction] = useState<
    (() => Promise<void>) | null
  >(null);

  const { allEvents, videoStart } = useManualReviewEvents();
  const state = useVideoControlState();

  // Throttle currentTime for expensive calculations that don't need frame-level precision
  const throttledCurrentTime = useThrottledValue(state.currentTime, 500);

  // Live playhead instant (distinct from the static viewingWindowStart).
  const viewingTime = useMemo(
    () => videoStart.add(throttledCurrentTime, "second"),
    [videoStart, throttledCurrentTime]
  );

  const nextEvent = useMemo(
    () => getNextEvent(allEvents, viewingTime.unix()),
    [allEvents, viewingTime]
  );
  const nextDestination = nextEvent?.area ?? null;

  const {
    pendingAreaSelection,
    clearPendingAreaSelection,
    handleAreaSelect,
    isAreaModeActive,
    isShiftHeld,
    binEntryActive,
    binBuffer,
    binEntryAvailable,
    filteredMaterials,
    isAreaMaterialMappingOpen,
    closeAreaMaterialMappingModal,
    editingSnapshot,
    currentAreaMaterialMapping,
    currentAreaMaterialLabels,
    miniMapAreaColors,
    handleAreaMaterialUpdate,
    handleSnapshotMappingUpdate,
    handleDeleteSnapshot,
    handleMappingSnapshotClick,
    editingSnapshotCurrentMapping,
    reportedMaterialsAtMappingTime,
  } = useManualReviewHotkeys({
    location,
    railcarSlotMaterials,
    onVerifyAndNext: async () => {
      if (verifyAction) {
        await verifyAction();
      }
    },
  });

  const handleVerifyActionReady = useCallback(
    (action: (() => Promise<void>) | null) => {
      setVerifyAction(() => action);
    },
    []
  );

  const isRailcarMovementsEnabled =
    useIsBehindFeatureFlag("rail_car_movements");
  const hasPublishedChargeSchedules = useHasPublishedChargeSchedules();

  const massEvents = useMemo(
    () => massEventsFromReviewEvents(reviewEvents),
    [reviewEvents]
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: "space-between",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box>
          {children}
          <PanelErrorBoundary panel="manual-review-video">
            <ManualReviewPlayerSection
              location={location}
              footageLocation={footageLocation}
              reviewPeriods={reviewPeriods}
              displayedReviewPeriods={displayedReviewPeriods}
              viewingWindowStart={viewingWindowStart}
              viewingWindowEnd={viewingWindowEnd}
              debouncedWindowStart={debouncedWindowStart}
              debouncedWindowEnd={debouncedWindowEnd}
              displayStart={displayStart}
              displayEnd={displayEnd}
              isEventsFetching={isEventsFetching}
              allReviewPeriods={allReviewPeriods}
              isVerifying={isVerifying}
              verifyPeriods={verifyPeriods}
              goToPeriod={goToPeriod}
              verifyPeriodStarts={verifyPeriodStarts}
              labellerTimelines={labellerTimelines}
              highlightColorsByEventId={highlightColorsByEventId}
              pendingAreaSelection={pendingAreaSelection}
              clearPendingAreaSelection={clearPendingAreaSelection}
              handleAreaSelect={handleAreaSelect}
              isAreaMaterialMappingOpen={isAreaMaterialMappingOpen}
              closeAreaMaterialMappingModal={closeAreaMaterialMappingModal}
              editingSnapshot={editingSnapshot}
              currentAreaMaterialMapping={currentAreaMaterialMapping}
              reportedMaterialsAtMappingTime={reportedMaterialsAtMappingTime}
              miniMapAreaColors={miniMapAreaColors}
              handleAreaMaterialUpdate={handleAreaMaterialUpdate}
              handleSnapshotMappingUpdate={handleSnapshotMappingUpdate}
              handleDeleteSnapshot={handleDeleteSnapshot}
              handleMappingSnapshotClick={handleMappingSnapshotClick}
              editingSnapshotCurrentMapping={editingSnapshotCurrentMapping}
              onVerifyActionReady={handleVerifyActionReady}
              isLoading={isEventsFetching}
            />
          </PanelErrorBoundary>
        </Box>
        {isRailcarMovementsEnabled ? (
          <RailcarTimeline videoStart={start} videoEnd={end} />
        ) : null}
        <HotkeyExplanation
          eventHotkeys={eventHotkeys}
          areaColors={miniMapAreaColors}
          materials={filteredMaterials}
          location={location}
          extraModifierControls={<ManualReviewAreaHotkeyModeToggle />}
          navigationWindowMinutes={navigationWindowMinutes}
          extraHotkeys={extraHotkeys}
        />
      </Stack>

      <Stack
        direction="column"
        spacing={2}
        sx={{
          width: MANUAL_REVIEW_SIDEBAR_WIDTH,
          height: "100%",
        }}
      >
        {isRailcarMovementsEnabled ? (
          <RailcarMovements startTime={start} endTime={end} />
        ) : null}
        {hasPublishedChargeSchedules ? (
          <ActiveChargeScheduleMaterials
            location={location}
            videoStart={start}
            videoEnd={end}
          />
        ) : null}
        <ManualReviewSidebar
          nextDestination={nextDestination}
          location={location}
          areaColors={miniMapAreaColors}
          areaMaterials={currentAreaMaterialLabels}
          viewingWindowStart={start}
          viewingTime={viewingTime}
          massEvents={massEvents}
          heats={heats}
          loadingWindows={loadingWindows}
          dayStart={dayStart}
          dayEnd={dayEnd}
          extraTabs={sidebarExtraTabs}
        />
      </Stack>

      <LabellingOverlays
        isAreaModeActive={isAreaModeActive}
        isShiftHeld={isShiftHeld}
        binEntryActive={binEntryActive}
        binBuffer={binBuffer}
        binEntryAvailable={binEntryAvailable}
        areaColors={miniMapAreaColors}
        areaMaterials={currentAreaMaterialLabels}
        location={location}
      />
    </Box>
  );
};
